import React, { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import PublicNavbar from "../components/PublicNavbar";
import Footer from "../components/Footer";
import { openInteractiveDemo } from "../components/demo/DemoLauncher";
import { HELP_ARTICLES, ARTICLE_CATEGORIES } from "../data/helpContent";
import { ArrowLeft, ArrowRight, BookOpen, Clock, Check, Play } from "lucide-react";

const CAT_LABEL = Object.fromEntries(ARTICLE_CATEGORIES.map((c) => [c.key, c.label]));

export default function HelpArticle() {
  const { slug } = useParams();
  const article = HELP_ARTICLES.find((a) => a.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (article) document.title = `${article.title} | Junago`;
  }, [slug, article]);

  if (!article) return <Navigate to="/assistance" replace />;

  const related = [
    ...HELP_ARTICLES.filter((a) => a.slug !== slug && a.category === article.category),
    ...HELP_ARTICLES.filter((a) => a.slug !== slug && a.category !== article.category),
  ].slice(0, 3);

  return (
    <div className="min-h-screen bg-white">
      <PublicNavbar />

      <article className="max-w-3xl mx-auto px-6 pt-12 md:pt-16 pb-16">
        <Link
          to="/assistance"
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-violet-600 hover:text-violet-800 transition-colors mb-8"
          data-testid="article-back-link"
        >
          <ArrowLeft className="h-4 w-4" /> Centre d'assistance
        </Link>

        <div className="flex items-center gap-3 mb-5">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-violet-50 text-violet-700 text-[11px] font-semibold">
            <BookOpen className="h-3 w-3" /> {CAT_LABEL[article.category]}
          </span>
          <span className="inline-flex items-center gap-1 text-xs text-slate-400">
            <Clock className="h-3 w-3" /> Lecture {article.readTime}
          </span>
        </div>

        <h1
          className="font-heading text-4xl sm:text-5xl font-medium tracking-tight leading-[1.08] text-slate-900"
          data-testid="article-title"
        >
          {article.title}
        </h1>
        <p className="mt-4 text-lg text-slate-600 leading-relaxed">{article.excerpt}</p>

        <div className="mt-12 space-y-12">
          {article.sections.map((s) => (
            <section key={s.heading} data-testid={`article-section-${s.heading.slice(0, 20)}`}>
              <h2 className="font-heading text-2xl font-medium tracking-tight text-slate-900 mb-4">
                {s.heading}
              </h2>
              {s.paragraphs?.map((p, i) => (
                <p key={i} className="text-[15px] text-slate-700 leading-relaxed mb-4">
                  {p}
                </p>
              ))}
              {s.bullets && (
                <ul className="mt-2 space-y-2.5">
                  {s.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-3 text-[15px] text-slate-700">
                      <span className="mt-1 h-5 w-5 rounded-full bg-violet-100 flex items-center justify-center shrink-0">
                        <Check className="h-3 w-3 text-violet-600" strokeWidth={3} />
                      </span>
                      {b}
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ))}
        </div>

        {/* CTA inline */}
        <div className="mt-16 rounded-2xl bg-slate-950 text-white p-8 relative overflow-hidden" data-testid="article-cta">
          <div aria-hidden className="absolute -right-16 -bottom-20 h-56 w-56 rounded-full bg-violet-600 opacity-30 blur-3xl" />
          <div className="relative">
            <h3 className="font-heading text-xl font-semibold tracking-tight">
              Prêt à essayer par vous-même&nbsp;?
            </h3>
            <p className="mt-2 text-sm text-slate-400 leading-relaxed">
              30 jours d'essai gratuit, sans carte bancaire et sans engagement.
            </p>
            <div className="mt-5 flex flex-col sm:flex-row gap-3">
              <Link
                to="/register?context=vendor"
                className="btn-jn-light inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold"
                data-testid="article-cta-trial"
              >
                Commencer gratuitement <ArrowRight className="btn-arrow h-4 w-4" />
              </Link>
              <button type="button" onClick={openInteractiveDemo} className="btn-jn-play-wrap" data-testid="article-cta-demo">
                <span className="btn-jn-play btn-jn-play--light"><Play className="h-4 w-4 fill-current" /></span>
                <span className="btn-jn-play-label btn-jn-play-label--light">Voir la démo</span>
              </button>
            </div>
          </div>
        </div>
      </article>

      {/* Related articles */}
      <section className="bg-slate-50 border-t border-slate-200" data-testid="article-related-section">
        <div className="max-w-6xl mx-auto px-6 py-16">
          <h2 className="font-heading text-2xl font-medium tracking-tight text-slate-900 mb-8">
            À lire aussi
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {related.map((a) => (
              <Link
                key={a.slug}
                to={`/assistance/article/${a.slug}`}
                className="group rounded-2xl border border-slate-200 bg-white p-6 hover:border-violet-300 hover:shadow-[0_12px_32px_-12px_rgba(124,58,237,0.25)] transition-all"
                data-testid={`related-article-${a.slug}`}
              >
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-violet-50 text-violet-700 text-[11px] font-semibold mb-4">
                  <BookOpen className="h-3 w-3" /> {CAT_LABEL[a.category]}
                </span>
                <h3 className="font-heading text-base font-semibold text-slate-900 leading-snug group-hover:text-violet-700 transition-colors">
                  {a.title}
                </h3>
                <div className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-violet-600">
                  Lire <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
